import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  Alert,
  ActivityIndicator,
  Dimensions,
  Animated,
  KeyboardAvoidingView,
  Platform
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useAuth } from '../../contexts/AuthContext';
import FormInput from '../../components/common/FormInput';
import { validateParticipant } from '../../api/auth';
import { H1, H2, H3, Body1, Body2, Caption } from '../../components/common/TextStyles';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

const RegisterScreen = ({ navigation }) => {
  const { register, error, clearError } = useAuth();
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [step, setStep] = useState(1); // 1: ID Discovery, 2: Profile Setup
  const [validated, setValidated] = useState(false);
  const [dobRequired, setDobRequired] = useState(false);

  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(20)).current;

  const [formData, setFormData] = useState({
    name: '',
    firstName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    participantNumber: '',
    dob: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 600,
        useNativeDriver: true,
      })
    ]).start();
  }, [step]);

  const handleValidateParticipant = async () => {
    // Accept any 3–20 alphanumeric character ID (letters & digits, case-insensitive)
    const trimmed = formData.participantNumber.trim();
    if (!trimmed || !/^[A-Za-z0-9]{3,20}$/.test(trimmed)) {
      setErrors({ ...errors, participantNumber: 'ID must be 3–20 letters/digits (e.g. KE123456789 or 789)' });
      return;
    }

    setValidating(true);
    setErrors({});

    try {
      const response = await validateParticipant(
        formData.participantNumber.trim().toUpperCase(),
        formData.firstName.trim()
      );
      if (response.success) {
        setFormData({
          ...formData,
          name: response.data.name || '',
          dob: response.data.dob || '',
          participantNumber: response.data.participantNumber
        });
        setDobRequired(!response.data.dob);
        setValidated(true);
        // Automatically move to step 2 after a brief delay for visual feedback
        setTimeout(() => setStep(2), 500);
      }
    } catch (err) {
      // Always show the full server message in an Alert for clarity
      const errorMessage = typeof err === 'string' ? err : (err.message || 'Participant not found');
      Alert.alert('Validation Error', errorMessage);
    } finally {
      setValidating(false);
    }
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.phone) newErrors.phone = 'Phone is required';
    if (dobRequired && !formData.dob) newErrors.dob = 'Date of birth is required';
    if (!formData.password) newErrors.password = 'Password is required';
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    return newErrors;
  };

  const handleSubmit = async () => {
    clearError();
    const validationErrors = validateStep2();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);
      await register({
        name: formData.name,
        email: formData.email.toLowerCase(),
        password: formData.password,
        phone: formData.phone,
        dob: formData.dob,
        participantNumber: formData.participantNumber.trim().toUpperCase()
      });
    } catch (err) {
      // err may be a string (thrown from api/auth.js) or an Error object (wrapped by AuthContext)
      const message = typeof err === 'string' ? err : (err.message || 'Please check your details and try again');
      Alert.alert('Registration Failed', message);
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <Animated.View style={{ opacity: fadeAnim, transform: [{ translateY: slideAnim }] }}>
      <View style={styles.infoGraphicContainer}>
        <LinearGradient
          colors={['#EEF2FF', '#E0E7FF']}
          style={styles.infoGraphic}
        >
          <Ionicons name="finger-print" size={48} color="#4F46E5" />
          <H3 style={styles.infoGraphicTitle}>Verify Your Identity</H3>
          <Body2 style={styles.infoGraphicText}>
            Enter your Participant ID and First Name provided by your FCP.
            It can be your full ID or just the last 3 digits.
          </Body2>
        </LinearGradient>
      </View>

      <FormInput
        label="Participant Number"
        placeholder="e.g. KE123456789 or last 3 digits"
        value={formData.participantNumber}
        onChangeText={(text) => setFormData({ ...formData, participantNumber: text.trim() })}
        error={errors.participantNumber}
        autoCapitalize="characters"
        maxLength={20}
      />

      <FormInput
        label="First Name"
        placeholder="As registered with FCP"
        value={formData.firstName}
        onChangeText={(text) => setFormData({ ...formData, firstName: text })}
        error={errors.firstName}
        autoCapitalize="words"
      />

      <TouchableOpacity
        style={[styles.primaryButton, validating && styles.buttonDisabled]}
        onPress={handleValidateParticipant}
        disabled={validating}
      >
        {validating ? (
          <ActivityIndicator color="#fff" />
        ) : (
          <>
            <Text style={styles.buttonText}>Connect ID</Text>
            <Ionicons name="arrow-forward" size={20} color="#fff" />
          </>
        )}
      </TouchableOpacity>
    </Animated.View>
  );

  const renderStep2 = () => (
    <Animated.View style={{ opacity: fadeAnim, transform: [{ translateY: slideAnim }] }}>
      <View style={styles.profileHeader}>
        <View style={styles.profileAvatar}>
          <Ionicons name="person" size={40} color="#4F46E5" />
          {validated && (
            <View style={styles.verifiedBadge}>
              <Ionicons name="checkmark-circle" size={24} color="#10B981" />
            </View>
          )}
        </View>
        <H2 style={styles.userName}>{formData.name || 'Digital Citizen'}</H2>
        <Caption style={styles.userMeta}>
          {formData.participantNumber} • {formData.dob ? new Date(formData.dob).toLocaleDateString() : 'Verify DOB'}
        </Caption>
      </View>

      <FormInput
        label="Email Address"
        placeholder="name@example.com"
        value={formData.email}
        onChangeText={(text) => setFormData({ ...formData, email: text })}
        error={errors.email}
        keyboardType="email-address"
        autoCapitalize="none"
      />

      <FormInput
        label="Phone Number"
        placeholder="0712 345 678"
        value={formData.phone}
        onChangeText={(text) => setFormData({ ...formData, phone: text })}
        error={errors.phone}
        keyboardType="phone-pad"
      />
      
      {dobRequired ? (
        <FormInput
          label="Date of Birth"
          placeholder="YYYY-MM-DD (e.g. 2000-01-01)"
          value={formData.dob}
          onChangeText={(text) => setFormData({ ...formData, dob: text })}
          error={errors.dob}
          keyboardType="numeric"
        />
      ) : null}

      <View style={styles.row}>
        <View style={{ flex: 1, marginRight: 8 }}>
          <FormInput
            label="Password"
            placeholder="••••••••"
            value={formData.password}
            onChangeText={(text) => setFormData({ ...formData, password: text })}
            error={errors.password}
            secureTextEntry
          />
        </View>
        <View style={{ flex: 1, marginLeft: 8 }}>
          <FormInput
            label="Confirm"
            placeholder="••••••••"
            value={formData.confirmPassword}
            onChangeText={(text) => setFormData({ ...formData, confirmPassword: text })}
            error={errors.confirmPassword}
            secureTextEntry
          />
        </View>
      </View>

      <TouchableOpacity
        style={[styles.primaryButton, loading && styles.buttonDisabled]}
        onPress={handleSubmit}
        disabled={loading}
      >
        {loading ? (
          <ActivityIndicator color="#fff" />
        ) : (
          <Text style={styles.buttonText}>Complete Registration</Text>
        )}
      </TouchableOpacity>

      <TouchableOpacity
        style={styles.backButton}
        onPress={() => setStep(1)}
      >
        <Text style={styles.backButtonText}>Use a different ID</Text>
      </TouchableOpacity>
    </Animated.View>
  );

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={styles.container}
    >
      <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.scrollContent}>
        <View style={styles.header}>
          <LinearGradient
            colors={['#4F46E5', '#6366F1']}
            style={styles.headerGradient}
          />
          <View style={styles.headerTop}>
            <TouchableOpacity onPress={() => navigation.goBack()} style={styles.iconCircle}>
              <Ionicons name="chevron-back" size={24} color="#FFF" />
            </TouchableOpacity>
            <H2 style={styles.headerTitle}>Join Youth Connect</H2>
            <View style={{ width: 44 }} />
          </View>

          <View style={styles.stepperContainer}>
            <View style={[styles.stepDot, step >= 1 && styles.stepDotActive]} />
            <View style={[styles.stepLine, step >= 2 && styles.stepLineActive]} />
            <View style={[styles.stepDot, step >= 2 && styles.stepDotActive]} />
          </View>
        </View>

        <View style={styles.formCard}>
          {step === 1 ? renderStep1() : renderStep2()}
        </View>

        <TouchableOpacity
          style={styles.footerLink}
          onPress={() => navigation.navigate('Login')}
        >
          <Text style={styles.footerText}>
            Already a member? <Text style={styles.footerTextBold}>Sign In</Text>
          </Text>
        </TouchableOpacity>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  scrollContent: {
    flexGrow: 1,
    paddingBottom: 30,
  },
  header: {
    height: 150,
    justifyContent: 'flex-end',
    paddingBottom: 25,
  },
  headerGradient: {
    ...StyleSheet.absoluteFillObject,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    marginBottom: 15,
  },
  iconCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    color: '#FFF',
    fontSize: 18,
    fontWeight: '800',
  },
  stepperContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 40,
  },
  stepDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: 'rgba(255,255,255,0.3)',
  },
  stepDotActive: {
    backgroundColor: '#FFF',
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.5)',
  },
  stepLine: {
    flex: 1,
    height: 2,
    backgroundColor: 'rgba(255,255,255,0.3)',
    marginHorizontal: 8,
  },
  stepLineActive: {
    backgroundColor: '#FFF',
  },
  formCard: {
    backgroundColor: '#FFF',
    marginHorizontal: 16,
    marginTop: -20,
    borderRadius: 24,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.05,
    shadowRadius: 12,
    elevation: 8,
  },
  infoGraphicContainer: {
    marginBottom: 18,
  },
  infoGraphic: {
    padding: 16,
    borderRadius: 16,
    alignItems: 'center',
  },
  infoGraphicTitle: {
    color: '#1F2937',
    fontSize: 16,
    marginVertical: 6,
  },
  infoGraphicText: {
    color: '#6B7280',
    textAlign: 'center',
    lineHeight: 18,
    fontSize: 12,
  },
  primaryButton: {
    backgroundColor: '#4F46E5',
    height: 48,
    borderRadius: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
  },
  buttonText: {
    color: '#FFF',
    fontSize: 14,
    fontWeight: '700',
    marginRight: 8,
  },
  buttonDisabled: {
    backgroundColor: '#9CA3AF',
  },
  profileHeader: {
    alignItems: 'center',
    marginBottom: 20,
  },
  profileAvatar: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#EEF2FF',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  verifiedBadge: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    backgroundColor: '#FFF',
    borderRadius: 10,
  },
  userName: {
    color: '#111827',
    fontSize: 18,
    marginBottom: 2,
  },
  userMeta: {
    color: '#6B7280',
    fontSize: 12,
  },
  row: {
    flexDirection: 'row',
  },
  backButton: {
    marginTop: 15,
    alignItems: 'center',
  },
  backButtonText: {
    color: '#6B7280',
    fontSize: 13,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  footerLink: {
    marginTop: 25,
    alignItems: 'center',
  },
  footerText: {
    color: '#6B7280',
    fontSize: 13,
  },
  footerTextBold: {
    color: '#4F46E5',
    fontWeight: '700',
  },
});

export default RegisterScreen;
